import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthContext";
import { apiGet } from "../api/client";
import { Logo } from "./Logo";

export function Nav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const { data: pending } = useQuery({
    queryKey: ["pending-instructor-count"],
    queryFn: () => apiGet<unknown[]>("/api/admin/instructors/pending"),
    enabled: user?.role === "admin",
    refetchInterval: 60_000,
    retry: false,
  });

  // Marks the AI markers did not agree on, waiting for this teacher's decision.
  const { data: markReviews } = useQuery({
    queryKey: ["mark-reviews"],
    queryFn: () => apiGet<unknown[]>("/api/tutor/mark-reviews"),
    enabled: user?.role === "instructor" || user?.role === "admin",
    refetchInterval: 60_000,
    retry: false,
  });

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      setMenuOpen(false);
      menuButtonRef.current?.focus();
    }

    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [menuOpen]);

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  const path = location.pathname;

  function isActive(prefix: string) {
    return path === prefix || path.startsWith(prefix + "/");
  }

  return (
    <header className="main-nav">
      <Link to="/" className="nav-brand" aria-label="PyDialog Home">
        <Logo size={26} />
        <span className="nav-brand-name">
          Py<span className="nav-brand-accent">Dialog</span>
        </span>
      </Link>

      {/* Hamburger button for mobile */}
      <button
        ref={menuButtonRef}
        className="nav-hamburger"
        onClick={() => setMenuOpen((v) => !v)}
        aria-label={menuOpen ? "Close menu" : "Open menu"}
        aria-expanded={menuOpen}
        aria-controls="primary-navigation"
      >
        <span className={`hamburger-bar${menuOpen ? " open" : ""}`} />
        <span className={`hamburger-bar${menuOpen ? " open" : ""}`} />
        <span className={`hamburger-bar${menuOpen ? " open" : ""}`} />
      </button>

      <div id="primary-navigation" className={`nav-menu${menuOpen ? " nav-menu-open" : ""}`}>
        <nav className="nav-links" aria-label="Primary navigation">
          {user ? (
            <>
              {(user.role === "learner" || user.role === "admin") && (
                <Link to="/learn" className={isActive("/learn") ? "active" : ""}>
                  {user.role === "admin" ? "All Subjects" : "My Subjects"}
                </Link>
              )}

              {user.role === "learner" && (
                <Link to="/browse" className={isActive("/browse") ? "active" : ""}>
                  Browse Courses
                </Link>
              )}

              {user.role === "learner" && (
                <Link to="/progress" className={isActive("/progress") ? "active" : ""}>
                  My Progress
                </Link>
              )}

              {(user.role === "instructor" || user.role === "admin") && (
                <>
                  <Link
                    to="/tutor"
                    className={
                      isActive("/tutor") &&
                      !path.startsWith("/tutor/learners") &&
                      !path.startsWith("/tutor/courses") &&
                      !path.startsWith("/tutor/marks")
                        ? "active"
                        : ""
                    }
                  >
                    {user.role === "admin" ? "Teaching Overview" : "Dashboard"}
                  </Link>
                  <Link
                    to="/tutor/learners"
                    className={path.startsWith("/tutor/learners") ? "active" : ""}
                  >
                    Roster
                  </Link>
                  <Link
                    to="/tutor/courses"
                    className={path.startsWith("/tutor/courses") ? "active" : ""}
                  >
                    {user.role === "admin" ? "All Courses" : "My Courses"}
                  </Link>
                  <Link
                    to="/tutor/marks"
                    className={path.startsWith("/tutor/marks") ? "active" : ""}
                  >
                    Marks to review
                    {!!markReviews?.length && (
                      <span className="nav-count" aria-label={`${markReviews.length} waiting`}>
                        {markReviews.length}
                      </span>
                    )}
                  </Link>
                </>
              )}

              {user.role === "admin" && (
                <Link to="/admin" className={isActive("/admin") ? "active" : ""}>
                  Admin
                  {!!pending?.length && (
                    <span className="nav-count" aria-label={`${pending.length} waiting`}>
                      {pending.length}
                    </span>
                  )}
                </Link>
              )}
            </>
          ) : (
            <>
              <Link to="/how-it-works" className={isActive("/how-it-works") ? "active" : ""}>
                How It Works
              </Link>
              <Link to="/caps" className={isActive("/caps") ? "active" : ""}>
                CAPS Alignment
              </Link>
              <Link to="/mastery" className={isActive("/mastery") ? "active" : ""}>
                Mastery Model
              </Link>
              <Link to="/for-educators" className={isActive("/for-educators") ? "active" : ""}>
                For Educators
              </Link>
            </>
          )}
        </nav>

        <div className="nav-actions">
          {user ? (
            <div className="nav-user">
              {user.student_number && (
                <span className="nav-student-number" title="Student Number">
                  {user.student_number}
                </span>
              )}
              <Link
                to="/account"
                className={`nav-username${isActive("/account") ? " active" : ""}`}
                title="Account Settings"
              >
                {user.name}
              </Link>
              <span className="nav-role badge">{user.role}</span>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={handleLogout}
              >
                Sign out
              </button>
            </div>
          ) : (
            <div className="nav-guest-actions">
              <Link to="/login" className="btn btn-sm btn-ghost">
                Sign in
              </Link>
              <Link to="/register" className="btn btn-sm btn-primary">
                Get started
              </Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
